/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React, {useState, Fragment} from 'react'
import PropTypes from 'prop-types'
import {FormattedMessage} from 'react-intl'
import {
    Alert,
    Button,
    Stack,
    Text,
    Link as ChakraLink,
    Image,
    Box
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {AlertIcon} from '@salesforce/retail-react-app/app/components/icons'
import {noop} from '@salesforce/retail-react-app/app/utils/utils'
import RegistrationFields from '../forms/registration-fields'
import Link from '@salesforce/retail-react-app/app/components/link'
import {getAssetUrl} from '@salesforce/pwa-kit-react-sdk/ssr/universal/utils'
import {
    apiErrorMessage,
    brandLogo,
    createAccountButton,
    formErrorMessageStyle,
    loyaltyHeader,
    styleAccountBenefits,
    termsPrivacyInfo,
    termsPrivacyInfoLink,
    topHeader
} from './index.style'
import ContentRenderer from '../content-renderer'
import Recaptcha from '../recaptcha'
import useRegistrationFields from '../forms/useRegistrationFields'
import {Controller} from 'react-hook-form'

const RegisterForm = ({submitForm, clickSignIn = noop, form}) => {
    const fields = useRegistrationFields({form, prefix: ''})

    const handleRecaptchaToken = (token, onChange) => {
        onChange(token)
    }

    return (
        <Fragment>
            <Stack justify="center" align="center" spacing={8}>
                {/* brand logo */}
                <Image
                    sx={brandLogo}
                    src={getAssetUrl('static/img/global/My_Cavenders-Logo.png')}
                />

                {/* loyalty message */}
                <Text sx={loyaltyHeader} as={'h2'} align="center">
                    <FormattedMessage
                        defaultMessage="Loyalty is rewarded here"
                        id="register_form.heading.loyaltyIsRewardedHere"
                    />
                </Text>

                {/* info */}
                <Text sx={topHeader} as={'p'} align="center">
                    <FormattedMessage
                        defaultMessage="Joining is free. Earn 1 Point for every dollar you spend on purchases. For every 200 Points receive a $5 Reward."
                        id="register_form.message.joiningMessage"
                    />
                </Text>

                {/* other info */}
                <Text sx={topHeader} as={'p'} align="center">
                    <FormattedMessage
                        defaultMessage="Plus, enjoy these additional benefits, just for members."
                        id="register_form.message.enjoyMessage"
                    />
                </Text>
            </Stack>

            {/* account benefits */}
            <ContentRenderer
                color="white"
                fontFamily="Montserrat"
                contentIds="create-account-benefits"
                sx={styleAccountBenefits}
                isSlot={false}
            />

            <form
                onSubmit={form.handleSubmit(submitForm)}
                data-testid="sf-auth-modal-form-register"
                noValidate
            >
                <Stack>
                    {form.formState.errors?.global && (
                        <Alert status="error" borderColor={'cav_red'}>
                            <AlertIcon color="cav_red" boxSize={4} />
                            <Text sx={apiErrorMessage} mt={0}>
                                {form.formState.errors.global.message}
                            </Text>
                        </Alert>
                    )}

                    {/* form input fileds */}
                    <RegistrationFields form={form} />

                    {/* recaptcha */}
                    <Controller
                        name="recaptcha"
                        rules={fields?.recaptcha.rules}
                        control={fields?.recaptcha.control}
                        render={({field: {onChange, value, ref}}) => (
                            <Recaptcha
                                onVerify={(token) => handleRecaptchaToken(token, onChange)}
                                onExpired={() => {
                                    onChange('')
                                }}
                            />
                        )}
                    />
                    {fields?.recaptcha?.error && (
                        <Box sx={formErrorMessageStyle}>
                            <AlertIcon aria-hidden="true" mr={2} />
                            {fields?.recaptcha?.error?.message}
                        </Box>
                    )}

                    {/* terms and condition info */}
                    <Text sx={termsPrivacyInfo}>
                        <FormattedMessage
                            id="register_form.message.terms_privacy_info"
                            defaultMessage="By creating an account, you are agreeing to the <terms>Terms and Conditions</terms> of the My Cavender’s Program and Cavender’s <policy>Privacy Policy</policy>, including consent to receive emails and/or automated SMS text messages about program updates, exclusive offers, and marketing communications. Msg & data rates may apply."
                            values={{
                                policy: (chunks) => (
                                    <ChakraLink
                                        as={Link}
                                        to="/privacy-policy.html"
                                        sx={termsPrivacyInfoLink}
                                    >
                                        {chunks}
                                    </ChakraLink>
                                ),

                                terms: (chunks) => (
                                    <ChakraLink
                                        as={Link}
                                        to="/terms.html"
                                        sx={termsPrivacyInfoLink}
                                    >
                                        {chunks}
                                    </ChakraLink>
                                )
                            }}
                        />
                    </Text>

                    <Button
                        type="submit"
                        onClick={() => form.clearErrors('global')}
                        isLoading={form.formState.isSubmitting}
                        sx={createAccountButton}
                    >
                        <FormattedMessage
                            defaultMessage="Create Account >>"
                            id="register_form.button.create_account_button"
                        />
                    </Button>
                </Stack>
            </form>
        </Fragment>
    )
}

RegisterForm.propTypes = {
    submitForm: PropTypes.func,
    clickSignIn: PropTypes.func,
    form: PropTypes.object
}

export default RegisterForm
